import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import getClassName from 'src/common/utils/getClassName';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import UnreadIndicator from 'src/common/components/UnreadIndicator';
import { ImmutableTrip } from '../../../types/trips/Trips';
import { MOMENT_FORMAT } from '../../../common/constants';
import { TRIP_STATES, WITH_CAPTAIN_LABEL } from '../../constants';
import { guestsString } from '../../helpers';
import { getOffer } from '../../../common/utils/reduxStoreSelectors';
import { captainedGetter } from '../cards/Captain/selector';
import { guestsGetter, sumGuests } from '../cards/GroupSize/selector';
import NotificationMessage from '../../../common/components/NotificationMessage';
import Svg from '../../../common/components/Svg';
import OnlineStatus from '../OnlineStatus';

import s from './TripCardDetails.module.scss';
import useCaptainData from '../../../common/hooks/useCaptainData';

type Props = {
  trip: ImmutableTrip;
  name: string;
  disabled: boolean;
  disabledString: string;
  /* This modifier is used to apply specific styles for renters,
     such as different positioning of the title wrap element.
     When modifiers for other roles are needed, they can be added here. */
  classNameModifier?: 'renter';
  recipientIsOnline: boolean;
  isOwner: boolean;
  unreadIndicator?: boolean;
  unreadMessageCount?: number;
};

const TripCardDetails: FC<Props> = ({
  children,
  disabled = false,
  disabledString,
  name,
  trip,
  classNameModifier,
  recipientIsOnline,
  isOwner,
  unreadIndicator,
  unreadMessageCount = 0,
}) => {
  const offer = useSelector(getOffer);
  const { tripOnly: { startDateTimeMoment, durationAsWords } } = useSelector(dateTimeDurationSelector(trip.get('pk')));
  const captained = captainedGetter({ trip, offer });

  const dateTimeString = `${startDateTimeMoment.format(MOMENT_FORMAT.DATE)} • ${durationAsWords}`;
  const hasCalendarConflicts = trip.get('has_calendar_conflicts');
  const tripState = trip.getIn(['state', 'state']);
  const isOfferCancelled = (
    tripState === TRIP_STATES.OWNER_OFFER_CANCELLED
    || tripState === TRIP_STATES.OWNER_OFFER_AUTO_CANCELLED
  );
  const isOfferExpired = (
    tripState === TRIP_STATES.OWNER_OFFER_EXPIRED
    || tripState === TRIP_STATES.OWNER_CUSTOM_OFFER_EXPIRED
  );

  const { captainOptionHeading } = useCaptainData(trip);
  const hasConflict = hasCalendarConflicts && !isOfferCancelled && !isOfferExpired;
  const ownerActionRequired = isOwner && captainOptionHeading.shortLabel === WITH_CAPTAIN_LABEL
    && !captainOptionHeading.key;

  return (
    <div
      data-test="TripCardDetails"
      className={getClassName(s, 'root', `${disabled && 'disabled'} ${classNameModifier}`)}
    >
      {disabled ? (
        <NotificationMessage
          classNameModifier="alignTop gray"
          outdent={false}
        >
          <ul className={s.info}>
            <li className={s.disabledTitle}>{disabledString}</li>
            <li>{name}</li>
            <li>{dateTimeString}</li>
          </ul>
        </NotificationMessage>
      ) : (
        <>
          <div className={getClassName(s, 'titleWrap', classNameModifier)}>
            {name && (
              <h4 className={s.name}>
                {name}
                {__DEV__ && <OnlineStatus>{recipientIsOnline ? 'online' : 'offline'}</OnlineStatus>}
              </h4>
            )}
            {(hasConflict || unreadIndicator) && (
              <div className={s.badges}>
                {hasConflict && (
                  <Svg
                    href="#exclamation-icon"
                    className={s.icon}
                  />
                )}
                {unreadIndicator && (
                  <UnreadIndicator
                    /*
                    * UX: We always show an unread indicator when there are unread messages,
                    * but only render a numeric badge when there is more than one unread.
                    * Passing `undefined` for `unreadCountLabel` intentionally signals to
                    * `UnreadIndicator` to render a dot / generic badge without a number,
                    * so a single unread message shows an unnumbered badge rather than "1".
                    */
                    unreadCountLabel={unreadMessageCount > 1
                      ? String(unreadMessageCount)
                      : undefined}
                  />
                )}
              </div>
            )}
          </div>
          <div className={s.wrapper}>
            <ul className={s.info}>
              <li>
                <time
                  dateTime={startDateTimeMoment.format(MOMENT_FORMAT.VALUE)}
                  className={getClassName(s, 'dateTime', classNameModifier)}
                >
                  {dateTimeString}
                </time>
              </li>
              <li>{guestsString(sumGuests(guestsGetter({ trip, offer })))}</li>
              {captained !== null && (
                <li
                  className={getClassName(
                    s,
                    'captainStatus',
                    classNames({ ownerActionRequired }),
                  )}
                >
                  {captainOptionHeading.shortLabel}
                </li>
              )}
            </ul>
            {children}
          </div>
        </>
      )}
    </div>
  );
};

export default TripCardDetails;
